import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Button,
  Collapse,
  Form,
  Input,
  Popconfirm,
  Space,
  Spin,
  Typography,
  message,
} from 'antd';
import { DeleteOutlined, MinusCircleOutlined, PlusOutlined } from '@ant-design/icons';
import {
  type FixedRupResource,
  type KrutRupResource,
  type TingantaFixedRupRow,
  type TingantaKrutRow,
  buildFixedRupPatch,
  buildKrutPatch,
  pairsFromKrutForm,
  krutRowToKeyValues,
  pickEmbeddedFixedRup,
  pickEmbeddedKrutRow,
  rowToFormValues,
  tingantaRelationEmbedKeys,
  tingantaKrut,
  tingantaNichKrut,
  tingantaNichKarmani,
  tingantaNichKartari,
  tingantaRupKarmani,
  tingantaRupKartari,
  tingantaSanKrut,
  tingantaSanKarmani,
  tingantaSanKartari,
  tingantaYangKrut,
  tingantaYangKarmani,
  tingantaYangKartari,
  tingantaYanglukKrut,
  tingantaYanglukKarmani,
  tingantaYanglukKartari,
} from '../api/tingantaRupApi';
import type { Tinganta } from '../api/tingantaApi';

function errMsg(err: unknown): string {
  const ax = err as { response?: { data?: { message?: string } }; message?: string };
  return (
    ax?.response?.data?.message || ax?.message || 'Request failed'
  );
}

type FixedConfig = {
  key: string;
  title: string;
  code: string;
  resource: FixedRupResource;
};

const FIXED_RUP_CONFIG: FixedConfig[] = [
  { key: 'rup-k', title: 'Rūpa kartari', code: 'tinganta-rup-kartari', resource: tingantaRupKartari },
  { key: 'rup-m', title: 'Rūpa karmani', code: 'tinganta-rup-karmani', resource: tingantaRupKarmani },
  { key: 'san-k', title: 'San kartari', code: 'tinganta-san-kartari', resource: tingantaSanKartari },
  { key: 'san-m', title: 'San karmani', code: 'tinganta-san-karmani', resource: tingantaSanKarmani },
  { key: 'nich-k', title: 'Ṇij kartari', code: 'tinganta-nich-kartari', resource: tingantaNichKartari },
  { key: 'nich-m', title: 'Ṇij karmani', code: 'tinganta-nich-karmani', resource: tingantaNichKarmani },
  { key: 'yang-k', title: 'Yaṅ kartari', code: 'tinganta-yang-kartari', resource: tingantaYangKartari },
  { key: 'yang-m', title: 'Yaṅ karmani', code: 'tinganta-yang-karmani', resource: tingantaYangKarmani },
  { key: 'yangluk-k', title: 'Yaṅ-luk kartari', code: 'tinganta-yangluk-kartari', resource: tingantaYanglukKartari },
  { key: 'yangluk-m', title: 'Yaṅ-luk karmani', code: 'tinganta-yangluk-karmani', resource: tingantaYanglukKarmani },
];

type KrutConfig = { key: string; title: string; code: string; resource: KrutRupResource };

const KRUT_CONFIG: KrutConfig[] = [
  { key: 'krut', title: 'Kṛt', code: 'tinganta-krut', resource: tingantaKrut },
  { key: 'san-krut', title: 'San kṛt', code: 'tinganta-san-krut', resource: tingantaSanKrut },
  { key: 'nich-krut', title: 'Ṇij kṛt', code: 'tinganta-nich-krut', resource: tingantaNichKrut },
  { key: 'yang-krut', title: 'Yaṅ kṛt', code: 'tinganta-yang-krut', resource: tingantaYangKrut },
  { key: 'yangluk-krut', title: 'Yaṅ-luk kṛt', code: 'tinganta-yangluk-krut', resource: tingantaYanglukKrut },
];

function TingantaFixedRupPanelBody({
  tingantaId,
  detail,
  active,
  resource,
  code,
  onReload,
}: {
  tingantaId: number;
  detail: Tinganta | null;
  active: boolean;
  resource: FixedRupResource;
  code: string;
  onReload: () => Promise<void>;
}) {
  const [row, setRow] = useState<TingantaFixedRupRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<Record<string, string>>();

  const embedKeys = useMemo(() => tingantaRelationEmbedKeys(resource.basePath), [resource.basePath]);
  const detailRec = detail ? (detail as unknown as Record<string, unknown>) : null;

  const syncFromApi = useCallback(async () => {
    if (!tingantaId) return;
    const embedded = pickEmbeddedFixedRup(detailRec, embedKeys, tingantaId);
    if (embedded) {
      setRow(embedded);
      form.setFieldsValue(rowToFormValues(embedded, resource.scalarKeys));
      return;
    }
    setLoading(true);
    try {
      const rows = await resource.list(tingantaId);
      const r = rows[0] ?? null;
      setRow(r);
      form.setFieldsValue(rowToFormValues(r, resource.scalarKeys));
    } catch (err: unknown) {
      message.error(errMsg(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [detailRec, embedKeys, form, resource, tingantaId]);

  useEffect(() => {
    if (!active || !tingantaId) return;
    void syncFromApi();
  }, [active, tingantaId, syncFromApi]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      setSaving(true);
      if (row) {
        const patch = buildFixedRupPatch(row, values, resource.scalarKeys);
        if (Object.keys(patch).length === 0) {
          message.info('No changes');
          return;
        }
        await resource.update(row.id, patch);
        message.success('Updated');
      } else {
        await resource.create({ tingantaId, ...values });
        message.success('Created');
      }
      await onReload();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await resource.remove(row.id);
      message.success('Deleted');
      setRow(null);
      form.setFieldsValue(rowToFormValues(null, resource.scalarKeys));
      await onReload();
    } catch (err: unknown) {
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        <code>{code}</code> — at most one row per tiṅganta. Optional string fields.
      </Typography.Paragraph>
      {row ? (
        <Typography.Text type="secondary">
          Row id <strong>{row.id}</strong>
        </Typography.Text>
      ) : (
        <Typography.Paragraph type="secondary">No row yet — fill fields and save to create.</Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
        {resource.scalarKeys.map((k) => (
          <Form.Item key={k} name={k} label={k}>
            <Input.TextArea rows={2} placeholder="Optional" />
          </Form.Item>
        ))}
        <Space wrap>
          <Button type="primary" onClick={() => void onSave()} loading={saving}>
            {row ? 'Save changes' : 'Create'}
          </Button>
          {row ? (
            <Popconfirm title="Delete this row?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void onDelete()}>
              <Button danger icon={<DeleteOutlined />} loading={saving}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Form>
    </Spin>
  );
}

function TingantaKrutPanelBody({
  tingantaId,
  detail,
  active,
  resource,
  code,
  onReload,
}: {
  tingantaId: number;
  detail: Tinganta | null;
  active: boolean;
  resource: KrutRupResource;
  code: string;
  onReload: () => Promise<void>;
}) {
  const [row, setRow] = useState<TingantaKrutRow | null>(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form] = Form.useForm<{ pairs: { key: string; value: string }[] }>();

  const embedKeys = useMemo(() => tingantaRelationEmbedKeys(resource.basePath), [resource.basePath]);
  const detailRec = detail ? (detail as unknown as Record<string, unknown>) : null;

  const syncFromApi = useCallback(async () => {
    if (!tingantaId) return;
    const embedded = pickEmbeddedKrutRow(detailRec, embedKeys, tingantaId);
    if (embedded) {
      setRow(embedded);
      const pairs = krutRowToKeyValues(embedded);
      form.setFieldsValue({
        pairs: pairs.length > 0 ? pairs : [{ key: '', value: '' }],
      });
      return;
    }
    setLoading(true);
    try {
      const rows = await resource.list(tingantaId);
      const r = rows[0] ?? null;
      setRow(r);
      const pairs = krutRowToKeyValues(r);
      form.setFieldsValue({
        pairs: pairs.length > 0 ? pairs : [{ key: '', value: '' }],
      });
    } catch (err: unknown) {
      message.error(errMsg(err));
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [detailRec, embedKeys, form, resource, tingantaId]);

  useEffect(() => {
    if (!active || !tingantaId) return;
    void syncFromApi();
  }, [active, tingantaId, syncFromApi]);

  const onSave = async () => {
    try {
      const values = await form.validateFields();
      const pairs = pairsFromKrutForm(values.pairs);
      if (pairs.length === 0 && !row) {
        message.warning('Add at least one key/value pair to create.');
        return;
      }
      setSaving(true);
      if (row) {
        const patch = buildKrutPatch(row, pairs);
        if (Object.keys(patch).length === 0) {
          message.info('No changes');
          return;
        }
        await resource.update(row.id, patch);
        message.success('Updated');
      } else {
        const body: Record<string, unknown> = { tingantaId };
        for (const p of pairs) {
          const v = p.value?.trim();
          if (v) body[p.key] = v;
        }
        await resource.create(body as { tingantaId: number } & Record<string, unknown>);
        message.success('Created');
      }
      await onReload();
    } catch (err: unknown) {
      if ((err as { errorFields?: unknown }).errorFields) return;
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  const onDelete = async () => {
    if (!row) return;
    setSaving(true);
    try {
      await resource.remove(row.id);
      message.success('Deleted');
      setRow(null);
      form.setFieldsValue({ pairs: [{ key: '', value: '' }] });
      await onReload();
    } catch (err: unknown) {
      message.error(errMsg(err));
    } finally {
      setSaving(false);
    }
  };

  if (!active) return null;

  return (
    <Spin spinning={loading}>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        <code>{code}</code> — kṛt-style columns are free-form keys (e.g. Devanagari affix names). One row per
        tiṅganta.
      </Typography.Paragraph>
      {row ? (
        <Typography.Text type="secondary">
          Row id <strong>{row.id}</strong>
        </Typography.Text>
      ) : (
        <Typography.Paragraph type="secondary">No row yet.</Typography.Paragraph>
      )}
      <Form form={form} layout="vertical" preserve={false} style={{ marginTop: 12 }}>
        <Form.List name="pairs">
          {(fields, { add, remove }) => (
            <>
              {fields.map(({ key, name, ...rest }) => (
                <Space key={key} align="baseline" style={{ display: 'flex', marginBottom: 8 }} wrap>
                  <Form.Item {...rest} name={[name, 'key']} label="Key" style={{ flex: 1, minWidth: 140 }}>
                    <Input placeholder="e.g. ल्युट्" />
                  </Form.Item>
                  <Form.Item {...rest} name={[name, 'value']} label="Value" style={{ flex: 2, minWidth: 200 }}>
                    <Input.TextArea rows={2} placeholder="comma-separated forms" />
                  </Form.Item>
                  <MinusCircleOutlined onClick={() => remove(name)} style={{ color: '#ff4d4f', marginTop: 30 }} />
                </Space>
              ))}
              <Form.Item>
                <Button type="dashed" onClick={() => add()} block icon={<PlusOutlined />}>
                  Add field
                </Button>
              </Form.Item>
            </>
          )}
        </Form.List>
        <Space wrap>
          <Button type="primary" onClick={() => void onSave()} loading={saving}>
            {row ? 'Save changes' : 'Create'}
          </Button>
          {row ? (
            <Popconfirm title="Delete this row?" okText="Delete" okButtonProps={{ danger: true }} onConfirm={() => void onDelete()}>
              <Button danger icon={<DeleteOutlined />} loading={saving}>
                Delete
              </Button>
            </Popconfirm>
          ) : null}
        </Space>
      </Form>
    </Spin>
  );
}

export type TingantaRupSectionsProps = {
  tingantaId: number;
  detail: Tinganta | null;
  onReload: () => Promise<void>;
};

export function TingantaRupSections({ tingantaId, detail, onReload }: TingantaRupSectionsProps) {
  const [openKeys, setOpenKeys] = useState<string | string[]>([]);

  const fixedItems = useMemo(
    () =>
      FIXED_RUP_CONFIG.map((cfg) => ({
        key: cfg.key,
        label: (
          <span>
            {cfg.title} <Typography.Text type="secondary">({cfg.code})</Typography.Text>
          </span>
        ),
        children: (
          <TingantaFixedRupPanelBody
            tingantaId={tingantaId}
            detail={detail}
            active={Array.isArray(openKeys) ? openKeys.includes(cfg.key) : openKeys === cfg.key}
            resource={cfg.resource}
            code={cfg.code}
            onReload={onReload}
          />
        ),
      })),
    [detail, tingantaId, onReload, openKeys]
  );

  const krutItems = useMemo(
    () =>
      KRUT_CONFIG.map((cfg) => ({
        key: cfg.key,
        label: (
          <span>
            {cfg.title} <Typography.Text type="secondary">({cfg.code})</Typography.Text>
          </span>
        ),
        children: (
          <TingantaKrutPanelBody
            tingantaId={tingantaId}
            detail={detail}
            active={Array.isArray(openKeys) ? openKeys.includes(cfg.key) : openKeys === cfg.key}
            resource={cfg.resource}
            code={cfg.code}
            onReload={onReload}
          />
        ),
      })),
    [detail, tingantaId, onReload, openKeys]
  );

  return (
    <div style={{ marginTop: 16 }}>
      <Typography.Title level={5} style={{ marginBottom: 8 }}>
        Tiṅganta rūpa &amp; kṛt
      </Typography.Title>
      <Typography.Paragraph type="secondary" style={{ marginTop: 0 }}>
        Open a section to edit. Values are read from <code>GET /tinganta/:id</code> when nested there,
        otherwise from the list endpoints (optional <code>?tingantaId=</code>).
      </Typography.Paragraph>
      <Collapse
        accordion={false}
        activeKey={openKeys}
        onChange={(k) => setOpenKeys(k)}
        items={[...fixedItems, ...krutItems]}
      />
    </div>
  );
}
